import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import { getAllTrips } from './khubtripsSlice';

type ReturnedType = any;

const initialState = {
  isLoading: false,
  data: [],
  error: null
};

type driverIdInitialStateProps = {
  isLoading: boolean;
  driverIdData: any;
  error: null;
};

const driverIdDropDown: driverIdInitialStateProps = {
  isLoading: false,
  driverIdData: [],
  error: null
};

type contactNumberInitialStateProps = {
  isLoading: boolean;
  contactNumberData: any;
  error: null;
};

const contactNumberDropDown: contactNumberInitialStateProps = {
  isLoading: false,
  contactNumberData: [],
  error: null
};

type contactDataInitialStateProps = {
  isLoading: boolean;
  contactData: any;
  error: null;
};

const contactDataDropDown: contactDataInitialStateProps = {
  isLoading: false,
  contactData: [],
  error: null
};

type customerIdInitialStateProps = {
  isLoading: boolean;
  customerIdData: any;
  error: null;
};

const customerIdDropDown: customerIdInitialStateProps = {
  isLoading: false,
  customerIdData: [],
  error: null
};

export const addDeliveryTripsAction = createAsyncThunk<ReturnedType, any>(
  'add/trips',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_DELIVERY_TRIP}`,
        data
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Add my trips slice
const addDeliveryTrips = createSlice({
  name: 'add trips',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(addDeliveryTripsAction.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        addDeliveryTripsAction.fulfilled,
        (state, action: PayloadAction<ReturnedType>) => {
          state.isLoading = false;
          state.data = action.payload?.data;
          state.error = null;
        }
      )
      .addCase(addDeliveryTripsAction.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload.response?.data?.error || 'Something went wrong';
      });
  }
});

// get driverID dropdown
export const getDriverId = createAsyncThunk<any, any>(
  'driver/id',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DRIVER_DROPDOWN}?tripStart=${data.tripStart}&tripEnd=${data.tripEnd}&vehicleNumber=${data.vehicleNumber}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get driver id slice
export const driverIdSlice = createSlice({
  name: 'getDriverId',
  initialState: driverIdDropDown,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDriverId.pending, state => {
        state.isLoading = true;
        state.driverIdData = [];
      })
      .addCase(getDriverId.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.driverIdData = action.payload;
        state.error = null;
      })
      .addCase(getDriverId.rejected, (state, action: any) => {
        state.isLoading = false;
        state.driverIdData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get contact number dropdown
export const getContactNumber = createAsyncThunk<any>(
  'contact/number',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.CONTACT_NUMBER}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get contact number slice
export const contactNumberSlice = createSlice({
  name: 'getContactNumber',
  initialState: contactNumberDropDown,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getContactNumber.pending, state => {
        state.isLoading = true;
        state.contactNumberData = [];
      })
      .addCase(getContactNumber.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.contactNumberData = action.payload;
        state.error = null;
      })
      .addCase(getContactNumber.rejected, (state, action: any) => {
        state.isLoading = false;
        state.contactNumberData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// get data based on contact number
export const getContactData = createAsyncThunk<any, any>(
  'contact/data',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.CONTACT_DATA}?contactNumber=${data}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get data based on contact number slice
export const contactDataSlice = createSlice({
  name: 'getContactData',
  initialState: contactDataDropDown,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getContactData.pending, state => {
        state.isLoading = true;
        state.contactData = [];
      })
      .addCase(getContactData.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.contactData = action.payload;
        state.error = null;
      })
      .addCase(getContactData.rejected, (state, action: any) => {
        state.isLoading = false;
        state.contactData = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

// update delivery trips
export const updateDeliveryTrips = createAsyncThunk<ReturnedType, any>(
  'update/deliveryTrips',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_DELIVERY}?tripId=${data.tripID}`,
        data: data.updatePayload
      });
      // await thunkApi.dispatch(addDeliveryTripsAction());
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// update delibvery slice
const updateDeliverySlice = createSlice({
  name: 'updateTrailer',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateDeliveryTrips.pending, state => {
      state.isLoading = true;
      state.data = [];
    });
    builder.addCase(
      updateDeliveryTrips.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      }
    );
    builder.addCase(updateDeliveryTrips.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload.response.data.error || 'Something went wrong';
    });
  }
});

// get all delivery trips and orders
export const getDeliveryAndOrders = createAsyncThunk<ReturnedType>(
  'delivery/orders',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DELIVERY_AND_ORDERS}`
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// get all delivery and orders
const getDeliveryAndOrdersSlice = createSlice({
  name: 'getDeliveryAndOrders',
  initialState: initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getDeliveryAndOrders.pending, state => {
        state.isLoading = true;
        state.data = [];
      })
      .addCase(getDeliveryAndOrders.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        const { data } = action.payload;
        state.data = data?.map((item: any) => ({
          ...item,
          id: item.id
        }));
        state.error = null;
      })
      .addCase(getDeliveryAndOrders.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });
  }
});

//action for order delivery status
export const getOrderStatus = createAsyncThunk<any, any>(
  'orderstatus',
  async (data, thunkApi) => {
    try {
      const response = axiosInstance({
        method: 'GET',
        url: `${URLs.ORDER_STATUS}tripId=${data}`
      });
      return response;
    } catch (error: any) {}
  }
);
interface OrderPayload {
  pageno: number;
  pagesize: number;
  status: string;
}

//action for order by status
export const getOrderByStatus = createAsyncThunk<ReturnedType, any>(
  'orderbystatus/get',
  async (data, thunkApi) => {
    const { status, pageno, pagesize, signal } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.ORDER_BY_STATUS}status=${status}&pageno=${pageno}&pagesize=${pagesize}`,
        signal: signal || ''
      });
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      thunkApi.rejectWithValue(error);
    }
  }
);

const orderByStatusState = {
  isLoading: false,
  data: null,
  error: null,
  count: null
};

//slice for order by status
const getOrderByStatusSlice = createSlice({
  name: 'orderbystatus',
  initialState: orderByStatusState,
  reducers: {
    clearOrderByStatus: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getOrderByStatus.pending, state => {
        state.data = null;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderByStatus.fulfilled, (state, action: any) => {
        state.isLoading = false;
        // const {
        //   data: {
        //     data: { count, orderDetailsResponseList }
        //   }
        // } = action?.payload || {};
        state.data = action?.payload?.data?.data?.orderDetailsResponseList;
        state.count = action?.payload?.data?.data?.count;
        state.error = null;
      })
      .addCase(getOrderByStatus.rejected, (state, action: any) => {
        state.data = null;
        state.isLoading = false;
        state.error = action?.payload?.response?.data?.error;
      });
  }
});

export interface TripDropDown {
  isLoading: boolean;
  tripList: string[] | null;
  error: string | null;
}
const tripDropdownState: TripDropDown = {
  isLoading: false,
  tripList: null,
  error: null
};
//action for trip dropdown list
export const getTripsDropdown = createAsyncThunk(
  'reassign/dropdown',
  async (_, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.RESCHEDULE_TRIPS_LIST}`
      });
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for get trips dropdown
const getTripsDropdownSlice = createSlice({
  name: 'reassign',
  initialState: tripDropdownState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTripsDropdown.pending, state => {
        state.isLoading = true;
        state.tripList = null;
      })
      .addCase(getTripsDropdown.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.tripList = action?.payload;
      })
      .addCase(getTripsDropdown.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action?.payload?.response?.data?.error;
      });
  }
});

interface ReassignOrders {
  payload: { tripId: number; assignedOrderId: string[] };
  orderType: string;
}

//action for order Reassign
export const reassignTripOrders = createAsyncThunk<ReturnedType, ReassignOrders>(
  'orders/reassign',
  async (data, thunkApi) => {
    const { payload, orderType } = data;
    try {
      const response: any = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.TRIPS_ORDERS_REASSIGN}`,
        data: payload
      });

      if (response?.status === 200) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: 'Orders reassigned successfully',
            severity: 'success'
          })
        );
      }
      await thunkApi.dispatch(
        getOrderByStatus({ status: orderType, pageno: 1, pagesize: 8 })
      );
      return response;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );

      return thunkApi.rejectWithValue(error);
    }
  }
);
//slice for orders reassign for trips
const ordersReassignSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(reassignTripOrders.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.error = null;
      })
      .addCase(reassignTripOrders.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(reassignTripOrders.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = [];
        state.error = action?.payload?.response?.data?.error;
      });
  }
});
export interface DeliveryTripPayload {
  vehicleNumber: string;
  sourceAddress: string;
  sourceLat: number;
  sourceLng: number;
  customerList: CustomerAddress[];
}
interface CustomerAddress {
  deliveryAddress: string;
  deliverableLat: number;
  deliverableLng: number;
}

interface DeliveryTripPreview {
  isLoading: boolean;
  data: DeliveryPreviewResponse | null;
  error: string | null;
}

const deliveryTripPreviewState: DeliveryTripPreview = {
  isLoading: false,
  data: null,
  error: null
};

interface DeliveryPreviewResponse {
  totalDistanceInMeters: number;
  totalDurationInSeconds: number;
  encodedPolyLine: string;
  waypointDetails: null;
  customerlist: CustomerAddress[];
}

//action for deliveryTrip preview
export const previewDeliveryTrip = createAsyncThunk<ReturnedType, DeliveryTripPayload>(
  'preview/deliverytrip',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.DELIVERY_TRIP_PREVIEW}`,
        data
      });
      if (response?.status === 200) {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: response?.data?.message,
            severity: 'success'
          })
        );
      }
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for delivery Trip preview
const deliveryTripPreviewSlice = createSlice({
  name: 'preview',
  initialState: deliveryTripPreviewState,
  reducers: {
    clearDeliveryPreview: state => {
      state.data = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(previewDeliveryTrip.pending, state => {
        state.isLoading = true;
        state.data = null;
        state.error = null;
      })
      .addCase(
        previewDeliveryTrip.fulfilled,
        (state, action: PayloadAction<DeliveryPreviewResponse>) => {
          state.isLoading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addCase(previewDeliveryTrip.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = null;
        state.error = action.payload.response?.data?.error;
      });
  }
});
export const addDeliveryTripSlice = addDeliveryTrips.reducer;
export const driverIdReducer = driverIdSlice.reducer;
export const contactNumberReducer = contactNumberSlice.reducer;
export const contactDataReducer = contactDataSlice.reducer;
export const updateDeliveryReducer = updateDeliverySlice.reducer;
export const getDeliveryAndOrdersReducer = getDeliveryAndOrdersSlice.reducer;
export const getOrderByStatusData = getOrderByStatusSlice.reducer;
export const getTripsDropdownData = getTripsDropdownSlice.reducer;
export const ordersReassignData = ordersReassignSlice.reducer;
export const previewDeliveryTripsData = deliveryTripPreviewSlice.reducer;
export const { clearDeliveryPreview } = deliveryTripPreviewSlice.actions;
export const { clearOrderByStatus } = getOrderByStatusSlice.actions;
